import { FC, useEffect, useState } from 'react';
import Modal from '../../../../components/Modal';
import {
  useGetProviderCalendarSessionSyncSettingsQuery,
  useUpdateProviderCalendarSessionSyncSettingsMutation,
} from '../../../../../generated/graphql';
import { customToast } from '../../../../components/ToastAlert/customToast';
import { logger } from '../../../../../lib/logger';
const HEADER_COPY =
  'When a team member connects their Calendar to Homecoming, we automatically detect and create sessions to review from events that include your clients and team members are invitees. These settings control how that sync works.';

const MAIN_TOGGLE = {
  key: 'enableCalendarSyncForSessions' as const,
  label: 'Enable Calendar Sync for Sessions',
  description:
    'Toggle this to automatically create sessions to review from calendar events.',
};

const NESTED_OPTIONS = [
  {
    key: 'includeArchivedPatients' as const,
    label: 'Include Archived Clients',
    description: 'Include archived clients in session creation during sync.',
  },
  {
    key: 'syncCalendarSessionsForCollaborators' as const,
    label: 'Sync Calendar Sessions for Collaborators',
    description:
      'Include external collaborators in session creation during sync.',
  },
  {
    key: 'includeDeactivatedStaff' as const,
    label: 'Sync Calendar Sessions for Deactivated Staff',
    description: 'Include deactivated staff in session creation during sync.',
  },
] as const;

type SettingKey =
  | typeof MAIN_TOGGLE['key']
  | typeof NESTED_OPTIONS[number]['key'];

/** API defaults (server model); used before settings load and for rollback fallbacks */
const DEFAULT_FOR_SETTING: Record<SettingKey, boolean> = {
  enableCalendarSyncForSessions: false,
  includeArchivedPatients: true,
  syncCalendarSessionsForCollaborators: true,
  includeDeactivatedStaff: true,
};

interface SessionsSettingsModalProps {
  onClose: () => void;
}

const SessionsSettingsModal: FC<SessionsSettingsModalProps> = ({ onClose }) => {
  const { data, loading, error } =
    useGetProviderCalendarSessionSyncSettingsQuery();
  const [updateSettings, { loading: isUpdating }] =
    useUpdateProviderCalendarSessionSyncSettingsMutation();

  const [localValues, setLocalValues] = useState<Record<string, boolean>>({});

  const settings = data?.providerCalendarSessionSyncSettings;

  useEffect(() => {
    if (settings) {
      setLocalValues({
        enableCalendarSyncForSessions: settings.enableCalendarSyncForSessions,
        includeArchivedPatients: settings.includeArchivedPatients,
        syncCalendarSessionsForCollaborators:
          settings.syncCalendarSessionsForCollaborators,
        includeDeactivatedStaff: settings.includeDeactivatedStaff,
      });
    }
  }, [settings]);

  useEffect(() => {
    if (error) {
      customToast.error('Failed to load settings');
      onClose();
    }
  }, [error, onClose]);

  const enableCalendarSync =
    localValues.enableCalendarSyncForSessions ??
    DEFAULT_FOR_SETTING.enableCalendarSyncForSessions;
  const subTogglesDisabled = !enableCalendarSync;

  const settingsUnavailable =
    !loading && data?.providerCalendarSessionSyncSettings === null;
  const togglesDisabled = !settings || loading || isUpdating;

  const handleToggle = async (key: SettingKey, value: boolean) => {
    if (!settings) {
      customToast.error('Calendar session sync settings are unavailable.');
      return;
    }
    const previousValue = localValues[key] ?? DEFAULT_FOR_SETTING[key];

    setLocalValues((prev) => ({ ...prev, [key]: value }));

    try {
      const response = await updateSettings({
        variables: {
          input: {
            [key]: value,
          },
        },
      });

      if (response.data?.updateProviderCalendarSessionSyncSettings.success) {
        customToast.success('Settings updated');
      } else {
        customToast.error(
          response.data?.updateProviderCalendarSessionSyncSettings.error ||
            'Failed to update settings',
        );
        setLocalValues((prev) => ({ ...prev, [key]: previousValue }));
      }
    } catch (err) {
      logger.error(err);
      customToast.error('Failed to update settings');
      setLocalValues((prev) => ({ ...prev, [key]: previousValue }));
    }
  };

  if (error) {
    return null;
  }

  return (
    <Modal
      name="SessionsSettings"
      isOpen
      setClosed={onClose}
      width="medium"
      closeRowText="Calendar Session Sync Settings"
      fetching={loading}
    >
      <div className="px-4 pb-6 sm:px-6">
        <p className="mb-6 text-body text-neutral-125">{HEADER_COPY}</p>

        {settingsUnavailable && (
          <p
            className="mb-4 rounded border border-neutral-100 bg-neutral-25 px-3 py-2 text-caption text-neutral-125"
            role="status"
          >
            Calendar session sync settings are not available right now. You can
            close this dialog and try again later.
          </p>
        )}

        <div className="space-y-5">
          {/* Main toggle: Enable Calendar Sync */}
          <div className="flex flex-row items-center justify-between py-3">
            <div className="flex-1">
              <div
                id="sessions-settings-main-toggle-label"
                className="mb-1 text-caption font-medium text-green-150"
              >
                {MAIN_TOGGLE.label}
              </div>
              <div
                id="sessions-settings-main-toggle-desc"
                className="text-small-caption text-neutral-125"
              >
                {MAIN_TOGGLE.description}
              </div>
            </div>
            <label className="relative ml-4 inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={enableCalendarSync}
                disabled={togglesDisabled}
                onChange={(e) =>
                  handleToggle(MAIN_TOGGLE.key, e.target.checked)
                }
                className="peer sr-only"
                aria-labelledby="sessions-settings-main-toggle-label"
                aria-describedby="sessions-settings-main-toggle-desc"
              />
              <div className="relative h-6 w-11 rounded-full bg-neutral-100 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-neutral-100 after:bg-white after:transition-all after:content-[''] peer-checked:bg-secondary-125 peer-checked:after:translate-x-full peer-focus:ring-2 peer-focus:ring-secondary-125 peer-focus:ring-offset-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-60" />
            </label>
          </div>

          {/* Nested options */}
          <div
            className={`ml-4 border-l-2 border-neutral-75 pl-5 ${
              subTogglesDisabled ? 'opacity-60' : ''
            }`}
          >
            <div className="space-y-4">
              {NESTED_OPTIONS.map(({ key, label, description }) => {
                const value = localValues[key] ?? DEFAULT_FOR_SETTING[key];

                const labelId = `sessions-settings-nested-${key}-label`;
                const descId = `sessions-settings-nested-${key}-desc`;

                return (
                  <div key={key} className="flex flex-row py-2">
                    <div className="flex-1">
                      <div
                        id={labelId}
                        className="mb-0.5 text-caption font-medium text-green-150"
                      >
                        {label}
                      </div>
                      <div
                        id={descId}
                        className="text-small-caption text-neutral-125"
                      >
                        {description}
                      </div>
                    </div>
                    <div className="ml-4 flex items-center">
                      <input
                        id={key}
                        type="checkbox"
                        disabled={togglesDisabled || subTogglesDisabled}
                        checked={value}
                        onChange={(e) => handleToggle(key, e.target.checked)}
                        className="h-5 w-5 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-secondary-125 disabled:cursor-not-allowed"
                        aria-labelledby={labelId}
                        aria-describedby={descId}
                      />
                      <label
                        htmlFor={key}
                        className={`ml-2 text-caption text-neutral-125 ${
                          subTogglesDisabled
                            ? 'cursor-not-allowed'
                            : 'cursor-pointer'
                        }`}
                        aria-hidden="true"
                      >
                        {value ? 'On' : 'Off'}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SessionsSettingsModal;
